import { Menu, MenuItem } from "@ark-ui/solid";
import { createMemo, createSelector, createSignal, For, Show } from "solid-js";
import { useTranslation } from "../../../../state/i18n";
import { useUserState } from "../../../../state/user";
import { useAuthDispatch } from "../../../../state/auth";
import { Icon } from "../../../../components/icon/Icon";
import Text from "../../../../components/text/Text";
import { resolveFullName } from "../../../../utils/userUtil";
import { useCustomerState } from "../../../../state/customer";
import CustomerId from "../../../../state/CustomerId";
import { isEqual, isNil, omit } from "lodash-es";
import ApiService from "../../../../services/ApiService";
import IconListItem from "../../../../components/menu/listItems/IconListItem";
import ImageListItem from "../../../../components/menu/listItems/ImageListItem";
import { A } from "@solidjs/router";
import PermissionService from "../../../../services/PermissionService";
import FeatureService from "../../../../services/FeatureService";
import { keyFromSelector } from "i18next";
import moreappIcon from "../../../../assets/moreapp-icon-solid.svg";

import { MemberAvatar } from "../../../../components/avatar/Avatar.tsx";
import { IconButton } from "../../../../components/button/Button.tsx";
import CreateCustomerForm from "../../../onboarding/CreateCustomerForm.tsx";

const SWITCH_CUSTOMER_ID_PREFIX = "switch-customer-";
const CREATE_CUSTOMER_MENU_ITEM = "create-customer";

const UserMenu = () => {
  const { t } = useTranslation();
  const user = useUserState();
  const { logout } = useAuthDispatch();
  const [showCreateCustomer, setShowCreateCustomer] = createSignal(false);

  const fullName = createMemo(() => resolveFullName(user()));

  const activeCustomer = useCustomerState();
  const customersQuery = ApiService.getCustomers();

  const switchCustomer = (customerId: CustomerId) => {
    // Force a hard refresh
    window.open(`/customers/${customerId.toString()}`, "_self");
  };

  const firstAccessibleSettingsPage = createMemo(() => {
    const basePath = `/customers/${activeCustomer.customerId}/settings`;

    if (PermissionService.canManageApiKeys(activeCustomer.customerId)) {
      return `${basePath}/api-keys`;
    } else if (
      PermissionService.canManageWebhooks(activeCustomer.customerId) &&
      FeatureService.hasFeature("WEBHOOKS")
    ) {
      return `${basePath}/webhooks`;
    } else if (
      PermissionService.canManageEmailConfiguration(activeCustomer.customerId) &&
      FeatureService.hasFeature("SENDING_DOMAIN")
    ) {
      return `${basePath}/email-configuration`;
    } else if (PermissionService.canManageServiceAccounts(activeCustomer.customerId)) {
      return `${basePath}/service-accounts`;
    } else if (
      PermissionService.canManageSSO(activeCustomer.customerId) &&
      FeatureService.hasFeature("SINGLE_SIGN_ON")
    ) {
      return `${basePath}/sso`;
    }
  });

  return (
    <Menu.Root
      onSelect={({ value }) => {
        if (value === "logout") {
          void logout();
        } else if (value === CREATE_CUSTOMER_MENU_ITEM) {
          setShowCreateCustomer(true);
        } else if (value.startsWith(SWITCH_CUSTOMER_ID_PREFIX)) {
          void switchCustomer(CustomerId.from(value.substring(SWITCH_CUSTOMER_ID_PREFIX.length)));
        }
      }}
    >
      <Menu.Trigger
        asChild={(menuTriggerProps) => (
          <button
            {...menuTriggerProps}
            class="group inline-flex cursor-pointer items-center gap-0.5 rounded-3xl bg-button-bg-tertiary-default p-1 open:bg-button-bg-tertiary-active hover:bg-button-bg-tertiary-hover focus-visible:outline-default active:bg-button-bg-tertiary-pressed"
          >
            <span class="sr-only">{t(($) => $.USER_MENU_ARIA_LABEL)}: </span>
            <MemberAvatar member={user()} size="md" />
            <Icon
              name="chevronDown"
              type="solid"
              size="md"
              class="icon-button-icon-primary-harsh group-open:icon-button-icon-tertiary-active"
            />
          </button>
        )}
      />
      <Menu.Context>
        {(context) => (
          <>
            <Show when={showCreateCustomer()}>
              <CreateCustomerForm close={() => setShowCreateCustomer(false)} />
            </Show>
            <Show when={context().open}>
              <Menu.Positioner>
                <Menu.Content
                  class="z-10 w-86 divide-y divide-subtle rounded-sm bg-surface-default shadow-elevation-light-3 outline-none"
                  onKeyDown={() => {
                    // Ensure highlighted item is in view when using keyboard navigation, and it is outside the scroll container
                    // Wait until ArkUI has updated the highlighted value (otherwise we would scroll the old one into view)
                    setTimeout(() => {
                      const { highlightedValue } = context();
                      if (!isNil(highlightedValue) && highlightedValue.startsWith(SWITCH_CUSTOMER_ID_PREFIX)) {
                        document.getElementById(highlightedValue)?.scrollIntoView({ block: "nearest" });
                      }
                    });
                  }}
                >
                  <div class="flex gap-2 p-4">
                    <div class="shrink-0">
                      <MemberAvatar member={user()} size="lg" />
                    </div>
                    <div class="flex flex-col overflow-hidden">
                      <Text class="text-base-semibold text-primary" text={fullName()} truncate />
                      <Text class="text-base-regular text-secondary" text={user().username} truncate />
                    </div>
                  </div>

                  <div class="px-1 pt-4 pb-1.5">
                    <h3 class="ml-3 text-header-md text-secondary">{t(($) => $.USER_MENU_SETTINGS_TITLE)}</h3>
                    <div class="mt-1">
                      <MenuItem
                        value="account"
                        asChild={(menuItemProps) => (
                          <A href={`/customers/${activeCustomer.customerId}/settings/profile`} {...menuItemProps} />
                        )}
                      >
                        <IconListItem
                          htmlProps={null}
                          icon={{ name: "user", type: "solid" }}
                          label={t(($) => $.USER_MENU_ACCOUNT_TITLE)}
                        />
                      </MenuItem>
                      <Show when={PermissionService.canManageBilling(activeCustomer.customerId)}>
                        <MenuItem
                          value="billing"
                          asChild={(menuItemProps) => (
                            <A href={`/customers/${activeCustomer.customerId}/settings/billing`} {...menuItemProps} />
                          )}
                        >
                          <IconListItem
                            htmlProps={null}
                            icon={{ name: "creditCard", type: "solid" }}
                            label={t(($) => $.USER_MENU_BILLING_TITLE)}
                          />
                        </MenuItem>
                      </Show>
                      <Show when={firstAccessibleSettingsPage()}>
                        {(path) => (
                          <MenuItem
                            value="connections"
                            asChild={(menuItemProps) => <A href={path()} {...menuItemProps} />}
                          >
                            <IconListItem
                              htmlProps={null}
                              icon={{ name: "globeAlt", type: "solid" }}
                              label={t(($) => $.USER_MENU_CONNECTIONS_TITLE)}
                            />
                          </MenuItem>
                        )}
                      </Show>
                    </div>
                  </div>

                  <Show when={customersQuery.data}>
                    {(customers) => (
                      <div class="px-1 pt-4 pb-1.5">
                        <div class="flex justify-between px-4">
                          <h3 class="text-header-md text-secondary">
                            {t(($) => $.USER_MENU_ACCOUNTS_TITLE, { count: customers().length })}
                          </h3>
                          <Show when={PermissionService.canCreateCustomers(activeCustomer.customerId)}>
                            <Menu.Item
                              value={CREATE_CUSTOMER_MENU_ITEM}
                              asChild={(arkProps) => (
                                <IconButton
                                  {...omit(arkProps(), "children")}
                                  icon="plus"
                                  aria-label={t(($) => $.USER_MENU_CREATE_ACCOUNT_LABEL)}
                                  size="small"
                                  variant="secondary"
                                />
                              )}
                            />
                          </Show>
                        </div>
                        <div class="mt-1 max-h-54 overflow-y-scroll">
                          <For each={customers()}>
                            {(customer) => {
                              const isActive = createSelector(() => activeCustomer.customerId, isEqual);

                              return (
                                <Menu.Item
                                  value={SWITCH_CUSTOMER_ID_PREFIX + customer.customerId}
                                  asChild={(arkProps) => (
                                    <ImageListItem
                                      label={customer.name}
                                      description={customer.customerId.toString()}
                                      image={{ src: moreappIcon, alt: t(($) => $.MOREAPP_LOGO_IMG_ALT) }}
                                      htmlProps={{
                                        ...arkProps(),
                                        "aria-label": t(
                                          isActive(customer.customerId)
                                            ? keyFromSelector(($) => $.USER_MENU_ACCOUNTS_ACTIVE_ARIA_LABEL)
                                            : keyFromSelector(($) => $.USER_MENU_ACCOUNTS_SWITCH_ARIA_LABEL),
                                          {
                                            customerName: customer.name,
                                            customerId: customer.customerId,
                                          },
                                        ),
                                        "data-selected": isActive(customer.customerId),
                                      }}
                                    />
                                  )}
                                />
                              );
                            }}
                          </For>
                        </div>
                      </div>
                    )}
                  </Show>

                  <div class="p-1">
                    <Menu.Item
                      value="logout"
                      asChild={(arkProps) => (
                        <IconListItem
                          htmlProps={arkProps()}
                          label={t(($) => $.USER_MENU_ACTIONS_LOGOUT)}
                          icon={{ name: "logout", type: "solid" }}
                        />
                      )}
                    />
                  </div>
                </Menu.Content>
              </Menu.Positioner>
            </Show>
          </>
        )}
      </Menu.Context>
    </Menu.Root>
  );
};

export default UserMenu;
